/**
 * Pi product lightbox — animated slide slider inside product-modal.
 */
(function () {
  const LIGHTBOX_SELECTOR = 'product-modal.pi-pg-lightbox';
  const GALLERY_SELECTOR = 'media-gallery.pi-product-gallery';
  const ZOOM_MS = 450;
  const ZOOM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
  const SLIDE_ZOOM_STEP = 0.5;
  const SLIDE_PAN_THRESHOLD = 5;
  const SLIDE_ZOOM_ANIM_MS = 350;

  function isImageLightboxTrigger(el) {
    return !!el?.closest?.('.product__modal-opener--image');
  }

  function resolveToggle(trigger) {
    if (!trigger) return null;
    if (trigger.classList?.contains('product__media-toggle')) return trigger;
    return trigger.querySelector?.('.product__media-toggle') || trigger;
  }

  function getBestImgSrc(img) {
    const srcset = img.getAttribute('srcset');
    if (srcset) {
      const candidates = srcset.split(',').map((part) => {
        const bits = part.trim().split(/\s+/);
        return { url: bits[0], w: parseInt(bits[1], 10) || 0 };
      });
      candidates.sort((a, b) => b.w - a.w);
      if (candidates[0]?.url) return candidates[0].url;
    }
    return img.currentSrc || img.src;
  }

  function findSourceImg(trigger) {
    const opener =
      trigger?.closest?.('.product__modal-opener--image') ||
      trigger?.closest?.('.product__modal-opener');
    if (!opener) return trigger?.querySelector?.('img') || null;
    return opener.querySelector('.product__media img') || opener.querySelector('img');
  }

  function getMaxSrcsetWidth(img) {
    const srcset = img.getAttribute('srcset');
    if (!srcset) return 0;
    return srcset.split(',').reduce((max, part) => {
      const bits = part.trim().split(/\s+/);
      const w = parseInt(bits[1], 10) || 0;
      return Math.max(max, w);
    }, 0);
  }

  function getImageDimensions(img) {
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      return { w: img.naturalWidth, h: img.naturalHeight };
    }

    const attrW = parseInt(img.getAttribute('width'), 10);
    const attrH = parseInt(img.getAttribute('height'), 10);
    const maxSrcsetW = getMaxSrcsetWidth(img);

    if (maxSrcsetW && attrW && attrH) {
      return { w: maxSrcsetW, h: Math.round((maxSrcsetW / attrW) * attrH) };
    }

    if (attrW && attrH) {
      return { w: attrW, h: attrH };
    }

    const rect = img.getBoundingClientRect();
    return { w: Math.max(rect.width, 1), h: Math.max(rect.height, 1) };
  }

  function getZoomTargetRect(natW, natH, modal) {
    const viewport = getViewport(modal);
    if (!viewport) return null;

    const vp = viewport.getBoundingClientRect();
    const maxW = Math.max(1, vp.width);
    const maxH = Math.max(1, vp.height);

    let w = Math.min(maxW, natW);
    let h = (w / natW) * natH;
    if (h > maxH) {
      h = maxH;
      w = (h / natH) * natW;
    }

    return {
      top: vp.top + (vp.height - h) / 2,
      left: vp.left + (vp.width - w) / 2,
      width: w,
      height: h,
    };
  }

  function applyCloneRect(clone, rect) {
    clone.style.top = `${rect.top}px`;
    clone.style.left = `${rect.left}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
  }

  function cleanupZoom(modal) {
    document.querySelectorAll('.pi-pg-lightbox__zoom-clone').forEach((el) => el.remove());
    modal?.classList?.remove('is-zoom-opening');
    resetAllSlideZoom(modal);
    const viewport = modal ? getViewport(modal) : null;
    if (viewport) {
      viewport.style.opacity = '';
      viewport.classList.remove('is-zoom-reveal');
    }
  }

  function wrapSlideImages(modal) {
    getVisibleSlides(modal).forEach((slide) => {
      const img = slide.querySelector(':scope > img, :scope img.global-media-settings');
      if (!img || img.closest('.pi-pg-lightbox__zoom-stage')) return;

      const stage = document.createElement('div');
      stage.className = 'pi-pg-lightbox__zoom-stage';
      img.parentNode.insertBefore(stage, img);
      stage.appendChild(img);
    });
  }

  function resetStageZoom(stage) {
    if (!stage) return;
    stage.classList.remove('is-zoomed', 'can-pan', 'is-panning', 'is-animating');
    stage.style.removeProperty('--pi-lb-zoom-scale');
    stage.style.removeProperty('--pi-lb-pan-x');
    stage.style.removeProperty('--pi-lb-pan-y');
    delete stage.dataset.scale;
    delete stage.dataset.panX;
    delete stage.dataset.panY;
    delete stage.dataset.zoomLevel;
    delete stage.dataset.zoomSteps;
    delete stage.dataset.baseW;
    delete stage.dataset.baseH;
  }

  function resetAllSlideZoom(modal) {
    if (!modal) return;
    modal.querySelectorAll('.pi-pg-lightbox__zoom-stage').forEach(resetStageZoom);
    modal.classList.remove('is-slide-zoomed');
  }

  function getStageCenter(stage) {
    const rect = stage.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  function invalidateZoomSteps(stage) {
    delete stage.dataset.zoomSteps;
    delete stage.dataset.baseW;
    delete stage.dataset.baseH;
  }

  function cacheBaseSize(stage, img) {
    if (stage.dataset.baseW && stage.dataset.baseH) return;
    const scale = parseFloat(stage.dataset.scale || '1') || 1;
    const rect = img.getBoundingClientRect();
    stage.dataset.baseW = String(rect.width / scale);
    stage.dataset.baseH = String(rect.height / scale);
  }

  function getSourceWidth(img) {
    const data = parseInt(img.getAttribute('data-original-width'), 10);
    if (data > 0) return data;
    const attr = parseInt(img.getAttribute('width'), 10);
    if (attr > 0) return attr;
    return img.naturalWidth || 1;
  }

  function getMaxSlideScale(stage, img) {
    const base = getBaseImageSize(stage, img);
    if (!base.w) return 1;

    const sourceW = getSourceWidth(img);
    const nativeScale = sourceW / base.w;
    if (nativeScale <= 1.02) return 1;
    return Math.round(nativeScale * 100) / 100;
  }

  function buildZoomSteps(maxScale) {
    if (maxScale <= 1.02) return [1];

    const steps = [1];
    for (let scale = 1 + SLIDE_ZOOM_STEP; scale < maxScale - 0.01; scale += SLIDE_ZOOM_STEP) {
      steps.push(Math.round(scale * 100) / 100);
    }
    const roundedMax = Math.round(maxScale * 100) / 100;
    if (steps[steps.length - 1] !== roundedMax) steps.push(roundedMax);
    return steps;
  }

  function ensureZoomSteps(stage, img) {
    if (stage.dataset.zoomSteps) return JSON.parse(stage.dataset.zoomSteps);
    cacheBaseSize(stage, img);
    const steps = buildZoomSteps(getMaxSlideScale(stage, img));
    stage.dataset.zoomSteps = JSON.stringify(steps);
    return steps;
  }

  function getBaseImageSize(stage, img) {
    cacheBaseSize(stage, img);
    if (stage.dataset.baseW && stage.dataset.baseH) {
      return { w: parseFloat(stage.dataset.baseW), h: parseFloat(stage.dataset.baseH) };
    }
    const scale = parseFloat(stage.dataset.scale || '1') || 1;
    const rect = img.getBoundingClientRect();
    return { w: rect.width / scale, h: rect.height / scale };
  }

  function clampSlidePan(stage, img, scale, panX, panY) {
    const stageRect = stage.getBoundingClientRect();
    const base = getBaseImageSize(stage, img);
    const scaledW = base.w * scale;
    const scaledH = base.h * scale;
    const maxPanX = Math.max(0, (scaledW - stageRect.width) / 2);
    const maxPanY = Math.max(0, (scaledH - stageRect.height) / 2);

    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, panX)),
      y: Math.max(-maxPanY, Math.min(maxPanY, panY)),
    };
  }

  function setStageTransform(stage, scale, panX, panY) {
    stage.style.setProperty('--pi-lb-zoom-scale', String(scale));
    stage.style.setProperty('--pi-lb-pan-x', `${panX}px`);
    stage.style.setProperty('--pi-lb-pan-y', `${panY}px`);
    stage.dataset.scale = String(scale);
    stage.dataset.panX = String(panX);
    stage.dataset.panY = String(panY);
  }

  function updateStageZoomState(modal, stage, img, scale) {
    const isZoomed = scale > 1.01;
    const steps = ensureZoomSteps(stage, img);
    const maxScale = steps[steps.length - 1];
    const isMax = isZoomed && Math.abs(scale - maxScale) < 0.01;

    const stageRect = stage.getBoundingClientRect();
    const base = getBaseImageSize(stage, img);
    const canPan = isZoomed && (base.w * scale > stageRect.width + 1 || base.h * scale > stageRect.height + 1);

    stage.classList.toggle('is-zoomed', isZoomed);
    stage.classList.toggle('is-max-zoom', isMax);
    stage.classList.toggle('can-pan', canPan);

    if (isZoomed) modal.classList.add('is-slide-zoomed');
    else if (!modal.querySelector('.pi-pg-lightbox__zoom-stage.is-zoomed')) {
      modal.classList.remove('is-slide-zoomed');
    }
  }

  function focalPanAtPoint(stage, currentScale, panX, panY, newScale, focalX, focalY) {
    const sc = getStageCenter(stage);
    const lx = (focalX - sc.x - panX) / currentScale;
    const ly = (focalY - sc.y - panY) / currentScale;
    return {
      x: focalX - sc.x - lx * newScale,
      y: focalY - sc.y - ly * newScale,
    };
  }

  function applyStageZoomLevel(modal, stage, img, level, focalX, focalY, animate = true) {
    cacheBaseSize(stage, img);
    const steps = ensureZoomSteps(stage, img);
    const clampedLevel = Math.max(0, Math.min(level, steps.length - 1));
    const newScale = steps[clampedLevel];
    const currentScale = parseFloat(stage.dataset.scale || '1');
    const currentPanX = parseFloat(stage.dataset.panX || '0');
    const currentPanY = parseFloat(stage.dataset.panY || '0');

    let panX = 0;
    let panY = 0;
    if (newScale > 1.001) {
      const focal = focalPanAtPoint(stage, currentScale, currentPanX, currentPanY, newScale, focalX, focalY);
      const pan = clampSlidePan(stage, img, newScale, focal.x, focal.y);
      panX = pan.x;
      panY = pan.y;
    }

    stage.classList.toggle('is-animating', animate);
    if (animate) window.setTimeout(() => stage.classList.remove('is-animating'), SLIDE_ZOOM_ANIM_MS);

    setStageTransform(stage, newScale, panX, panY);
    stage.dataset.zoomLevel = String(clampedLevel);
    updateStageZoomState(modal, stage, img, newScale);
  }

  function stepStageZoom(modal, stage, img, direction, focalX, focalY) {
    let level = parseInt(stage.dataset.zoomLevel || '0', 10);
    const steps = ensureZoomSteps(stage, img);
    const maxLevel = steps.length - 1;

    if (direction > 0 && level < maxLevel) level += 1;
    else if (direction < 0 && level > 0) level -= 1;
    else return;

    applyStageZoomLevel(modal, stage, img, level, focalX, focalY);
  }

  function bindSlideZoom(modal) {
    wrapSlideImages(modal);

    if (modal.__piSlideZoomBound) return;
    modal.__piSlideZoomBound = true;

    const viewport = getViewport(modal);
    if (!viewport) return;

    let panState = null;
    let pinchState = null;

    viewport.addEventListener('click', (event) => {
      if (!modal.hasAttribute('open') || modal.classList.contains('is-zoom-opening')) return;
      if (modal.__piSuppressZoomClick) {
        modal.__piSuppressZoomClick = false;
        return;
      }

      const stage = event.target.closest('.pi-pg-lightbox__zoom-stage');
      const img = stage?.querySelector('img');
      if (!stage || !img) return;

      event.stopPropagation();

      const steps = ensureZoomSteps(stage, img);
      if (steps.length <= 1) return;

      const level = parseInt(stage.dataset.zoomLevel || '0', 10);
      const maxLevel = steps.length - 1;

      if (level >= maxLevel) {
        applyStageZoomLevel(modal, stage, img, 0, event.clientX, event.clientY);
      } else {
        stepStageZoom(modal, stage, img, 1, event.clientX, event.clientY);
      }
    });

    viewport.addEventListener('dblclick', (event) => {
      if (!modal.hasAttribute('open')) return;

      const stage = event.target.closest('.pi-pg-lightbox__zoom-stage');
      const img = stage?.querySelector('img');
      if (!stage || !img) return;

      event.preventDefault();
      event.stopPropagation();
      applyStageZoomLevel(modal, stage, img, 0, event.clientX, event.clientY);
    });

    viewport.addEventListener(
      'wheel',
      (event) => {
        if (!modal.hasAttribute('open')) return;

        const stage = event.target.closest('.pi-pg-lightbox__zoom-stage');
        const img = stage?.querySelector('img');
        if (!stage || !img) return;

        event.preventDefault();
        stepStageZoom(modal, stage, img, event.deltaY < 0 ? 1 : -1, event.clientX, event.clientY);
      },
      { passive: false }
    );

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (!modal.hasAttribute('open') || event.button !== 0) return;

        const stage = event.target.closest('.pi-pg-lightbox__zoom-stage');
        if (!stage) return;

        const scale = parseFloat(stage.dataset.scale || '1');
        if (scale <= 1.01) return;
        if (!stage.classList.contains('can-pan')) return;

        const img = stage.querySelector('img');
        if (!img) return;

        panState = {
          stage,
          img,
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          basePanX: parseFloat(stage.dataset.panX || '0'),
          basePanY: parseFloat(stage.dataset.panY || '0'),
          moved: false,
        };

        stage.classList.add('is-pressing');
        try {
          stage.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    const onPanMove = (event) => {
      if (panState && event.pointerId === panState.pointerId) {
        const dx = event.clientX - panState.startX;
        const dy = event.clientY - panState.startY;

        if (!panState.moved && (Math.abs(dx) > SLIDE_PAN_THRESHOLD || Math.abs(dy) > SLIDE_PAN_THRESHOLD)) {
          panState.moved = true;
          panState.stage.classList.remove('is-pressing');
          panState.stage.classList.add('is-panning');
        }

        if (!panState.moved) return;

        const scale = parseFloat(panState.stage.dataset.scale || '1');
        const pan = clampSlidePan(
          panState.stage,
          panState.img,
          scale,
          panState.basePanX + dx,
          panState.basePanY + dy
        );
        panState.stage.classList.remove('is-animating');
        setStageTransform(panState.stage, scale, pan.x, pan.y);
        event.preventDefault();
        return;
      }

      if (pinchState && event.pointerId === pinchState.pointerIdA) {
        pinchState.ax = event.clientX;
        pinchState.ay = event.clientY;
      } else if (pinchState && event.pointerId === pinchState.pointerIdB) {
        pinchState.bx = event.clientX;
        pinchState.by = event.clientY;
      }

      if (!pinchState) return;

      const dist = Math.hypot(pinchState.ax - pinchState.bx, pinchState.ay - pinchState.by);
      if (!pinchState.startDist) {
        pinchState.startDist = dist;
        return;
      }

      const ratio = dist / pinchState.startDist;
      const steps = ensureZoomSteps(pinchState.stage, pinchState.img);
      const startLevel = pinchState.startLevel;
      let targetLevel = Math.round(startLevel + (ratio - 1) * 2);
      targetLevel = Math.max(0, Math.min(targetLevel, steps.length - 1));

      if (targetLevel !== parseInt(pinchState.stage.dataset.zoomLevel || '0', 10)) {
        pinchState.stage.classList.remove('is-animating');
        applyStageZoomLevel(
          modal,
          pinchState.stage,
          pinchState.img,
          targetLevel,
          (pinchState.ax + pinchState.bx) / 2,
          (pinchState.ay + pinchState.by) / 2,
          false
        );
      }
    };

    const onPanEnd = (event) => {
      if (panState && event.pointerId === panState.pointerId) {
        panState.stage.classList.remove('is-panning', 'is-pressing');
        try {
          panState.stage.releasePointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
        if (panState.moved) modal.__piSuppressZoomClick = true;
        panState = null;
        return;
      }

      if (!pinchState) return;
      if (event.pointerId === pinchState.pointerIdA || event.pointerId === pinchState.pointerIdB) {
        pinchState = null;
      }
    };

    viewport.addEventListener('pointermove', onPanMove);
    viewport.addEventListener('pointerup', onPanEnd);
    viewport.addEventListener('pointercancel', onPanEnd);

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (!modal.hasAttribute('open')) return;
        if (viewport.__piPinchPointers?.size === 2) return;

        if (!viewport.__piPinchPointers) viewport.__piPinchPointers = new Map();
        viewport.__piPinchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (viewport.__piPinchPointers.size !== 2) return;

        const stage = event.target.closest('.pi-pg-lightbox__zoom-stage');
        const img = stage?.querySelector('img');
        if (!stage || !img) {
          viewport.__piPinchPointers.clear();
          return;
        }

        const pointers = Array.from(viewport.__piPinchPointers.values());
        const ids = Array.from(viewport.__piPinchPointers.keys());
        panState = null;

        pinchState = {
          stage,
          img,
          pointerIdA: ids[0],
          pointerIdB: ids[1],
          ax: pointers[0].x,
          ay: pointers[0].y,
          bx: pointers[1].x,
          by: pointers[1].y,
          startDist: 0,
          startLevel: parseInt(stage.dataset.zoomLevel || '0', 10),
        };
      },
      true
    );

    viewport.addEventListener('pointerup', (event) => {
      viewport.__piPinchPointers?.delete(event.pointerId);
      if (viewport.__piPinchPointers?.size === 0) viewport.__piPinchPointers.clear();
    });
  }

  function syncActiveSlideImage(modal) {
    const slides = getVisibleSlides(modal);
    const slide = slides[modal.__piLightboxIndex];
    const img = slide?.querySelector('img');
    if (!img) return;

    img.sizes = '100vw';
    const hiSrc = getBestImgSrc(img);
    if (hiSrc && img.currentSrc !== hiSrc) {
      img.src = hiSrc;
    }
  }

  function piOpenWithZoom(modal, trigger, origShow) {
    bindLightbox(modal);

    const toggle = resolveToggle(trigger);
    const sourceImg = findSourceImg(trigger);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!toggle || !sourceImg || prefersReduced) {
      origShow.call(modal, toggle || trigger);
      requestAnimationFrame(() => {
        const mediaId = toggle?.getAttribute?.('data-media-id');
        if (mediaId) modal.piGoToMediaId?.(mediaId, false);
        else modal.piRefresh?.();
      });
      return;
    }

    const startRect = sourceImg.getBoundingClientRect();
    const clone = document.createElement('img');
    clone.className = 'pi-pg-lightbox__zoom-clone';
    clone.alt = sourceImg.alt || '';
    clone.src = getBestImgSrc(sourceImg);
    applyCloneRect(clone, startRect);
    document.body.appendChild(clone);

    modal.classList.add('is-zoom-opening');
    const viewport = getViewport(modal);
    if (viewport) {
      viewport.classList.remove('is-zoom-reveal');
      viewport.style.opacity = '0';
    }

    origShow.call(modal, toggle);

    const mediaId = toggle.getAttribute('data-media-id');
    let finished = false;

    const finishZoom = () => {
      if (finished) return;
      finished = true;
      clone.remove();
      modal.classList.remove('is-zoom-opening');
      syncActiveSlideImage(modal);
      if (viewport) {
        viewport.style.opacity = '';
        viewport.classList.add('is-zoom-reveal');
        const onEnd = () => {
          viewport.classList.remove('is-zoom-reveal');
          viewport.removeEventListener('animationend', onEnd);
        };
        viewport.addEventListener('animationend', onEnd);
      }
      modal.piRefresh?.();
    };

    const runZoom = () => {
      layoutSlides(modal);
      if (mediaId) modal.piGoToMediaId?.(mediaId, false);

      const dims = getImageDimensions(clone.naturalWidth > 0 ? clone : sourceImg);
      const target = getZoomTargetRect(dims.w, dims.h, modal);
      if (!target) {
        finishZoom();
        return;
      }

      clone.style.transition = `top ${ZOOM_MS}ms ${ZOOM_EASE}, left ${ZOOM_MS}ms ${ZOOM_EASE}, width ${ZOOM_MS}ms ${ZOOM_EASE}, height ${ZOOM_MS}ms ${ZOOM_EASE}`;

      requestAnimationFrame(() => {
        applyCloneRect(clone, target);

        const onDone = () => finishZoom();
        clone.addEventListener(
          'transitionend',
          (e) => {
            if (e.propertyName === 'width') onDone();
          },
          { once: true }
        );
        window.setTimeout(onDone, ZOOM_MS + 120);
      });
    };

    const startAnimation = () => {
      requestAnimationFrame(() => requestAnimationFrame(runZoom));
    };

    if (clone.complete && clone.naturalWidth > 0) {
      startAnimation();
    } else {
      clone.addEventListener('load', startAnimation, { once: true });
      clone.addEventListener('error', startAnimation, { once: true });
    }
  }

  function getTrack(modal) {
    return modal.querySelector('[data-pi-lightbox-track]');
  }

  function getViewport(modal) {
    return modal.querySelector('.pi-pg-lightbox__viewport');
  }

  function getVisibleSlides(modal) {
    const track = getTrack(modal);
    if (!track) return [];
    return Array.from(track.querySelectorAll('.pi-pg-lightbox__slide')).filter((slide) => {
      return getComputedStyle(slide).display !== 'none';
    });
  }

  function layoutSlides(modal) {
    const viewport = getViewport(modal);
    const track = getTrack(modal);
    const slides = getVisibleSlides(modal);
    if (!viewport || !track || !slides.length) return 0;

    const slideWidth = viewport.clientWidth;
    viewport.style.setProperty('--pi-lb-slide-w', `${slideWidth}px`);

    slides.forEach((slide) => {
      slide.style.flex = `0 0 ${slideWidth}px`;
      slide.style.width = `${slideWidth}px`;
      slide.style.minWidth = `${slideWidth}px`;
      slide.style.maxWidth = `${slideWidth}px`;
    });

    track.style.width = `${slides.length * slideWidth}px`;
    return slideWidth;
  }

  function bindLightbox(modal) {
    const prevBtn = modal.querySelector('.pi-pg-lightbox__nav--prev');
    const nextBtn = modal.querySelector('.pi-pg-lightbox__nav--next');
    const counterCurrent = modal.querySelector('.pi-pg-lightbox__counter-current');
    const counterTotal = modal.querySelector('.pi-pg-lightbox__counter-total');

    if (!getTrack(modal) || !prevBtn || !nextBtn) return;

    bindSlideZoom(modal);

    if (!modal.__piPointerGuard) {
      modal.__piPointerGuard = true;
      modal.addEventListener(
        'pointerup',
        (event) => {
          if (!modal.classList.contains('pi-pg-lightbox') || !modal.hasAttribute('open')) return;
          if (modal.classList.contains('is-zoom-opening')) {
            event.stopPropagation();
            return;
          }
          if (
            event.target.closest(
              '.pi-pg-lightbox__nav, .pi-pg-lightbox__close, .pi-pg-lightbox__slide, .pi-pg-lightbox__zoom-stage, deferred-media, product-model'
            )
          ) {
            event.stopPropagation();
          }
        },
        true
      );
    }

    if (modal.__piLightboxIndex == null) modal.__piLightboxIndex = 0;

    function updateCounter() {
      const slides = getVisibleSlides(modal);
      const total = slides.length;
      const index = Math.min(modal.__piLightboxIndex, Math.max(0, total - 1));
      modal.__piLightboxIndex = index;

      if (counterCurrent) counterCurrent.textContent = total ? index + 1 : 0;
      if (counterTotal) counterTotal.textContent = total;

      prevBtn.toggleAttribute('disabled', index <= 0 || total < 2);
      nextBtn.toggleAttribute('disabled', index >= total - 1 || total < 2);
    }

    function goToIndex(index, animate = true) {
      const slides = getVisibleSlides(modal);
      const trackEl = getTrack(modal);
      if (!slides.length || !trackEl) return;

      resetAllSlideZoom(modal);

      const slideWidth = layoutSlides(modal);
      const clamped = Math.max(0, Math.min(index, slides.length - 1));
      modal.__piLightboxIndex = clamped;

      if (!animate) trackEl.classList.remove('is-animating');
      else trackEl.classList.add('is-animating');

      trackEl.style.transform = `translate3d(-${clamped * slideWidth}px, 0, 0)`;

      trackEl.querySelectorAll('.pi-pg-lightbox__slide').forEach((slide) => slide.classList.remove('is-active'));
      const active = slides[clamped];
      active?.classList.add('is-active');

      window.pauseAllMedia();
      const deferred = active?.querySelector('deferred-media, product-model');
      if (deferred?.loadContent) deferred.loadContent(false);

      updateCounter();
    }

    modal.piGoToMediaId = function (mediaId, animate = true) {
      const slides = getVisibleSlides(modal);
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      goToIndex(index >= 0 ? index : 0, animate);
    };

    modal.piRefresh = function () {
      layoutSlides(modal);
      goToIndex(modal.__piLightboxIndex, false);
    };

    if (!modal.__piLightboxEventsBound) {
      modal.__piLightboxEventsBound = true;

      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToIndex(modal.__piLightboxIndex - 1);
      });

      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        goToIndex(modal.__piLightboxIndex + 1);
      });

      modal.addEventListener('keyup', (e) => {
        if (!modal.hasAttribute('open')) return;
        if (e.code === 'ArrowLeft') goToIndex(modal.__piLightboxIndex - 1);
        if (e.code === 'ArrowRight') goToIndex(modal.__piLightboxIndex + 1);
      });

      const viewport = getViewport(modal);
      if (viewport) {
        new ResizeObserver(() => {
          if (modal.hasAttribute('open')) modal.piRefresh();
        }).observe(viewport);
      }
    }

    layoutSlides(modal);
    updateCounter();
    goToIndex(modal.__piLightboxIndex, false);
  }

  function initGalleryLightboxClicks(gallery) {
    if (gallery.__piLbClickBound) return;
    gallery.__piLbClickBound = true;

    gallery.addEventListener('click', (event) => {
      const opener = event.target.closest('.product__modal-opener--image');
      if (!opener || !gallery.contains(opener)) return;

      const activeItem = opener.closest('.product__media-item');
      if (activeItem && !activeItem.classList.contains('is-active')) return;

      const toggle = opener.querySelector('.product__media-toggle');
      const modalSelector = opener.getAttribute('data-modal');
      const modal = modalSelector ? document.querySelector(modalSelector) : null;
      if (!toggle || !modal?.show) return;

      event.preventDefault();
      event.stopPropagation();
      modal.show(opener);
    });
  }

  function patchProductModal() {
    const ProductModal = customElements.get('product-modal');
    if (!ProductModal || ProductModal.prototype.__piLightboxPatched) return;
    ProductModal.prototype.__piLightboxPatched = true;

    const origShow = ProductModal.prototype.show;
    const origShowActiveMedia = ProductModal.prototype.showActiveMedia;
    const origHide = ProductModal.prototype.hide;

    ProductModal.prototype.show = function (opener) {
      if (this.classList.contains('pi-pg-lightbox') && isImageLightboxTrigger(opener)) {
        return piOpenWithZoom(this, opener, origShow);
      }

      origShow.call(this, opener);
      if (!this.classList.contains('pi-pg-lightbox')) return;
      bindLightbox(this);
      requestAnimationFrame(() => {
        const toggle = resolveToggle(opener);
        const mediaId = toggle?.getAttribute?.('data-media-id');
        if (mediaId) this.piGoToMediaId?.(mediaId, false);
        else this.piRefresh?.();
      });
    };

    ProductModal.prototype.showActiveMedia = function () {
      if (!this.classList.contains('pi-pg-lightbox')) {
        return origShowActiveMedia.call(this);
      }
      bindLightbox(this);
      const mediaId = this.openedBy?.getAttribute('data-media-id');
      if (mediaId) this.piGoToMediaId?.(mediaId, false);
    };

    ProductModal.prototype.hide = function () {
      cleanupZoom(this);
      const track = getTrack(this);
      if (track) {
        track.classList.remove('is-animating');
        track.style.transform = '';
      }
      return origHide.call(this);
    };
  }

  function patchProductInfo() {
    const ProductInfo = customElements.get('product-info');
    if (!ProductInfo || ProductInfo.prototype.__piLightboxInfoPatched) return;
    ProductInfo.prototype.__piLightboxInfoPatched = true;

    const origUpdateMedia = ProductInfo.prototype.updateMedia;

    ProductInfo.prototype.updateMedia = function (html, variantFeaturedMediaId) {
      const modal = this.productModal;
      const isPiLightbox = modal?.classList.contains('pi-pg-lightbox');

      origUpdateMedia.call(this, html, variantFeaturedMediaId);

      if (!isPiLightbox) return;

      const track = getTrack(modal);
      const newTrack = html.querySelector('[data-pi-lightbox-track]');

      if (track && newTrack) {
        track.innerHTML = newTrack.innerHTML;
      }

      bindLightbox(modal);
      if (variantFeaturedMediaId) {
        modal.piGoToMediaId?.(variantFeaturedMediaId, false);
      } else {
        modal.piRefresh?.();
      }
    };
  }

  function initAll() {
    document.querySelectorAll(LIGHTBOX_SELECTOR).forEach(bindLightbox);
    document.querySelectorAll(GALLERY_SELECTOR).forEach(initGalleryLightboxClicks);
    patchProductModal();
    patchProductInfo();
  }

  function bootstrap() {
    if (customElements.get('product-modal')) {
      initAll();
    } else {
      customElements.whenDefined('product-modal').then(initAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.(LIGHTBOX_SELECTOR).forEach(bindLightbox);
    event.target.querySelectorAll?.(GALLERY_SELECTOR).forEach(initGalleryLightboxClicks);
  });
})();
